import React from 'react';
import { Link } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import clsx from 'clsx';

import useSharedStyles from './theme/pbdqShared.styles';
import { pbdqFont } from './theme/pbdqTokens';
import { sdsColors, sdsRadius, sdsBtnBase, SDS_RAILS } from './theme/solutionsDesignSystem';
import pbdqRoutes from './pbdqRoutes';

import PbdqNav from './components/PbdqNav.comp';
import PbdqFooter from './components/PbdqFooter.comp';
import { useAgentChat } from './components/PbdqAgentChatContext';
import PbdqSolutionsExplorer from './components/PbdqSolutionsExplorer.comp';
import PbdqGradientStripe from './components/PbdqGradientStripe.comp';
import SolutionsHero from '../../assets/images/PoweredByDQ/SolutionsHero.png';
import { PBDQ_SOLUTIONS } from './constants/pbdqSolutions.constants';

const useStyles = makeStyles(() => ({

    heroSection: {
        background: 'radial-gradient(70% 90% at 86% 10%, #DCE7F3 0%, rgba(220, 231, 243, 0) 60%), linear-gradient(180deg, #F4F6F9 0%, #EEF2F6 100%)',
        borderBottom: '1px solid #E1E6EC',
    },
    heroInner: {
        maxWidth: 1320,
        margin: '0 auto',
        padding: 'clamp(36px, 4.5vw, 64px) clamp(20px, 4vw, 56px) clamp(40px, 5vw, 64px)',
        display: 'flex',
        flexWrap: 'wrap',
        gap: '32px clamp(32px, 5vw, 80px)',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        boxSizing: 'border-box',
    },
    heroLeft: {
        flex: '1 1 440px',
        minWidth: 0,
        display: 'grid',
        gap: 20,
        alignContent: 'start',
    },
    heroTitle: {
        fontSize: 'clamp(36px, 4.2vw, 60px)',
        lineHeight: 1.04,
        letterSpacing: '-0.03em',
        fontWeight: 800,
        margin: 0,
        maxWidth: '18ch',
        color: '#1B2430',
        fontFamily: pbdqFont,
    },
    heroAccent: {
        color: '#ED6628',
    },
    heroLead: {
        fontSize: 'clamp(16px, 1.3vw, 18px)',
        lineHeight: 1.55,
        color: '#5B6470',
        margin: 0,
        maxWidth: '58ch',
        fontFamily: pbdqFont,
    },
    heroBtn: {
        justifySelf: 'start',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 10,
        height: 52,
        padding: '0 24px',
        borderRadius: 999,
        background: '#ED6628',
        color: '#FFFFFF',
        textDecoration: 'none',
        fontSize: 15,
        fontWeight: 700,
        fontFamily: pbdqFont,
        whiteSpace: 'nowrap',
        transition: 'background 200ms cubic-bezier(.32, .72, 0, 1)',
        '&:hover': {
            background: '#D5581D',
            color: '#FFFFFF',
            textDecoration: 'none',
        },
    },
    heroArt: {
        flex: '1 1 400px',
        minWidth: 0,
        maxWidth: 640,
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    heroImage: {
        width: '100%',
        height: 'auto',
        display: 'block',
    },

    solutionsSection: {
        background: '#FFFFFF',
    },
    solutionsInner: {
        maxWidth: 1320,
        margin: '0 auto',
        padding: 'clamp(36px, 4.5vw, 56px) clamp(20px, 4vw, 56px)',
        boxSizing: 'border-box',
    },
    sectionHeader: {
        display: 'grid',
        gap: 12,
        marginBottom: 36,
        textAlign: 'left',
    },
    listTitle: {
        fontSize: 'clamp(26px, 3vw, 42px)',
        lineHeight: 1.1,
        letterSpacing: '-0.025em',
        fontWeight: 800,
        margin: 0,
        color: '#1B2430',
        fontFamily: pbdqFont,
    },
    listLead: {
        fontSize: 16,
        lineHeight: 1.55,
        margin: 0,
        color: '#5B6470',
        fontFamily: pbdqFont,
    },

    alsoSection: {
        background: '#FFFFFF',
    },
    alsoInner: {
        maxWidth: 1320,
        margin: '0 auto',
        padding: 'clamp(32px, 4vw, 56px) clamp(20px, 4vw, 56px) 0',
        boxSizing: 'border-box',
    },
    comingSoonBanner: {
        margin: '28px 0 0',
        padding: '18px 22px',
        border: '1px dashed #D5D9E0',
        borderRadius: 16,
        fontSize: 14.5,
        lineHeight: 1.55,
        color: '#5B6470',
        fontFamily: pbdqFont,
    },
    comingSoonStrong: {
        color: '#1B2430',
        fontWeight: 700,
    },
    alsoCard: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '32px clamp(24px, 4vw, 48px)',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: 'clamp(28px, 3.5vw, 48px)',
        borderRadius: 24,
        background: '#172939',
        color: '#FFFFFF',
        boxSizing: 'border-box',
    },
    alsoLeft: {
        flex: '1.2 1 360px',
        minWidth: 0,
        display: 'grid',
        gap: 14,
        alignContent: 'center',
    },
    alsoTagRow: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: 10,
        alignItems: 'center',
    },
    alsoEyebrow: {
        fontSize: 13,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        fontWeight: 700,
        color: '#8AA3BC',
        fontFamily: pbdqFont,
    },
    managedPill: {
        display: 'inline-flex',
        alignItems: 'center',
        padding: '5px 10px',
        borderRadius: 999,
        background: 'rgba(255, 255, 255, 0.1)',
        color: '#FFFFFF',
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        whiteSpace: 'nowrap',
        fontFamily: pbdqFont,
    },
    alsoTitle: {
        fontSize: 'clamp(24px, 2.4vw, 32px)',
        lineHeight: 1.15,
        letterSpacing: '-0.02em',
        fontWeight: 800,
        margin: 0,
        color: '#FFFFFF',
        fontFamily: pbdqFont,
    },
    alsoTitleAccent: {
        color: sdsColors.accent,
    },
    alsoDesc: {
        fontSize: 15.5,
        lineHeight: 1.6,
        margin: 0,
        color: '#D9E3EE',
        maxWidth: '68ch',
        fontFamily: pbdqFont,
    },
    alsoDescHighlight: {
        color: '#FFFFFF',
        fontWeight: 600,
    },
    alsoBtn: {
        justifySelf: 'start',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        height: 48,
        padding: '0 22px',
        borderRadius: 999,
        background: '#FFFFFF',
        color: '#172939',
        textDecoration: 'none',
        fontSize: 15,
        fontWeight: 700,
        whiteSpace: 'nowrap',
        marginTop: 8,
        fontFamily: pbdqFont,
        transition: 'background 200ms cubic-bezier(.32,.72,0,1)',
        '&:hover': {
            background: '#EEF3F8',
            color: '#172939',
            textDecoration: 'none',
        },
    },
    alsoVideoWrap: {
        flex: '1 1 380px',
        minWidth: 0,
        aspectRatio: '1 / 1',
        position: 'relative',
        borderRadius: 16,
        overflow: 'hidden',
        background: '#000000',
        maxWidth: 460,
        width: '100%',
        boxShadow: '0 10px 30px rgba(0,0,0,0.3)',
        '@media (max-width: 960px)': {
            margin: '0 auto',
            maxWidth: 420,
        },
    },
    alsoVideoIframe: {
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        border: 0,
    },

    ctaSection: {
        marginTop: 'clamp(24px, 3vw, 40px)',
        background: '#F4F6F9',
        borderTop: '1px solid #E9ECF0',
    },
    ctaInner: {
        maxWidth: 1320,
        margin: '0 auto',
        padding: 'clamp(36px, 4.5vw, 56px) clamp(20px, 4vw, 56px)',
        display: 'flex',
        flexWrap: 'wrap',
        gap: '24px 48px',
        alignItems: 'center',
        justifyContent: 'space-between',
        boxSizing: 'border-box',
    },
    ctaTitle: {
        fontSize: 'clamp(28px, 3.2vw, 44px)',
        lineHeight: 1.08,
        letterSpacing: '-0.03em',
        fontWeight: 800,
        margin: 0,
        maxWidth: '20ch',
        color: '#1B2430',
        fontFamily: pbdqFont,
    },
    ctaBtn: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 10,
        height: 54,
        padding: '0 26px',
        borderRadius: 999,
        background: '#ED6628',
        color: '#FFFFFF',
        textDecoration: 'none',
        fontSize: 16,
        fontWeight: 700,
        fontFamily: pbdqFont,
        whiteSpace: 'nowrap',
        transition: 'background 200ms cubic-bezier(.32,.72,0,1)',
        '&:hover': {
            background: '#D5581D',
            color: '#FFFFFF',
            textDecoration: 'none',
        },
    },
}));

const SOLUTIONS = PBDQ_SOLUTIONS;

const ArrowDownIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 5v14M6 13l6 6 6-6" />
    </svg>
);
const ArrowRightIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M5 12h14M13 6l6 6-6 6" />
    </svg>
);

const PbdqSolutionsPageContent = () => {
    const shared = useSharedStyles();
    const classes = useStyles();
    const openAgentChat = useAgentChat();
    const scrollToSolutions = (e) => {
        e.preventDefault();
        const el = document.getElementById('solutions');
        if (el) {
            const navOffset = 90;
            const top = el.getBoundingClientRect().top + window.pageYOffset - navOffset;
            window.scrollTo({
                top,
                behavior: 'smooth',
            });
        }
    };

    return (
        <div className={shared.page} style={{ background: sdsColors.bg }}>
            <PbdqNav />
            {/* <PbdqGradientStripe sticky /> */}
            <section aria-label="Introduction" className={classes.heroSection}>
                <div className={classes.heroInner}>
                    <div className={classes.heroLeft}>
                        <h1 className={classes.heroTitle}>
                            Five solutions for measurable <span className={classes.heroAccent}>AI-digital capacity</span> building.
                        </h1>
                        <p className={classes.heroLead}>
                            From child online safety and AI-digital literacy to workforce skilling and program validation, Powered by DQ helps organizations identify needs, build targeted capabilities and demonstrate measurable improvement.
                        </p>
                        <a
                            href="#solutions"
                            onClick={scrollToSolutions}
                            className={classes.heroBtn}
                        >
                            Find your solution <ArrowRightIcon />
                        </a>
                    </div>
                    <div className={classes.heroArt}>
                        <img
                            src={SolutionsHero}
                            alt="Powered by DQ solutions process: Identify needs, Build capabilities, Measure improvement"
                            className={classes.heroImage}
                        />
                    </div>
                </div>
            </section>

            <PbdqSolutionsExplorer solutions={SOLUTIONS} />

            <section aria-label="Also available" className={classes.alsoSection}>
                <div className={classes.alsoInner}>
                    <div className={classes.alsoCard}>
                        <div className={classes.alsoLeft}>
                            <div className={classes.alsoTagRow}>
                                <span className={classes.alsoEyebrow}>Also available</span>
                                <span className={classes.managedPill}>Managed program</span>
                            </div>
                            <h3 className={classes.alsoTitle}>
                                <span className={classes.alsoTitleAccent}>#DQEveryChild</span> standard packages
                            </h3>
                            <p className={classes.alsoDesc}>
                                The ready-made digital citizenship programs <span className={classes.alsoDescHighlight}>for children age 8-12</span> behind the global movement that has reached more than 1.5 million children across 80+ countries — delivered through managed national or regional rollout, rather than through the platform.
                            </p>
                            <Link to={pbdqRoutes.dqEveryChild} className={classes.alsoBtn}>
                                See the packages →
                            </Link>
                        </div>
                        <div className={classes.alsoVideoWrap}>
                            <iframe
                                src="https://player.vimeo.com/video/399536926?badge=0&autopause=0&autoplay=1&muted=1&loop=1&player_id=0&app_id=58479"
                                title="#DQEveryChild"
                                allow="autoplay; fullscreen; picture-in-picture; clipboard-write"
                                allowFullScreen
                                className={classes.alsoVideoIframe}
                            />
                        </div>
                    </div>
                    {/* <p className={classes.comingSoonBanner}>
                        <strong className={classes.comingSoonStrong}>Coming soon to COS Assessments:</strong> COS Parent, Teacher, School Leader, and Ed-Tech Assessments. Register interest and we’ll tell you when each becomes available.
                    </p> */}
                </div>
            </section>

            <section aria-label="Next step" className={classes.ctaSection}>
                <div className={classes.ctaInner}>
                    <h3 className={classes.ctaTitle}>Start where the need is most urgent.</h3>
                    <Link to={pbdqRoutes.pricing} className={classes.ctaBtn}>
                        Configure and get pricing <ArrowRightIcon />
                    </Link>
                </div>
            </section>

            {/* <PbdqGradientStripe /> */}
            <PbdqFooter />
        </div>
    );
};

export default PbdqSolutionsPageContent;
